
import { useEffect, useRef } from 'react';

const CosmicBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas to full screen
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight * 3; // Make it longer than viewport to cover scrolling
    };
    
    window.addEventListener('resize', setCanvasSize);
    setCanvasSize();
    
    // Create stars
    const stars = Array.from({ length: 150 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius: Math.random() * 1.5,
      color: `rgba(${Math.random() * 155 + 100}, ${Math.random() * 155 + 100}, ${Math.random() * 255}, ${Math.random() * 0.8 + 0.2})`
    }));
    
    // Create flow lines
    const flowLines = Array.from({ length: 8 }, (_, i) => {
      const colorHue = Math.random() * 60 + 200; // Blue to purple range
      return {
        points: Array.from({ length: 100 }, (_, j) => ({
          x: (j / 100) * canvas.width,
          y: Math.sin(j / 10 + i) * 50 + (Math.random() * 100) + canvas.height / 3 + (i * 30)
        })),
        color: `hsla(${colorHue}, 80%, 60%, 0.3)`,
        width: Math.random() * 2 + 0.5
      };
    });
    
    // Create floating circles
    const circles = Array.from({ length: 12 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius: Math.random() * 30 + 10,
      color: `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.1)`,
      borderColor: `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`,
      speed: Math.random() * 0.2 + 0.1
    }));
    
    // Draw everything
    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw stars
      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = star.color;
        ctx.fill();
      });
      
      // Draw flow lines
      flowLines.forEach(line => {
        ctx.beginPath();
        ctx.moveTo(line.points[0].x, line.points[0].y);
        
        for (let i = 1; i < line.points.length; i++) {
          const xc = (line.points[i].x + line.points[i - 1].x) / 2;
          const yc = (line.points[i].y + line.points[i - 1].y) / 2;
          ctx.quadraticCurveTo(line.points[i - 1].x, line.points[i - 1].y, xc, yc);
        }
        
        ctx.strokeStyle = line.color;
        ctx.lineWidth = line.width;
        ctx.stroke();
      });
      
      // Draw circles
      circles.forEach(circle => {
        circle.y += circle.speed;
        if (circle.y > canvas.height) circle.y = 0;
        
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
        ctx.fillStyle = circle.color;
        ctx.strokeStyle = circle.borderColor;
        ctx.lineWidth = 1;
        ctx.fill();
        ctx.stroke();
      });
    };
    
    // Animate
    const animate = () => {
      draw();
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute top-0 left-0 w-full h-full pointer-events-none z-0 opacity-30"
    />
  );
};

export default CosmicBackground;
